import { useEffect, useRef, useState } from "react";

import { useQuery } from "@tanstack/react-query";

import { listUserDocumentScanFields } from "services";

import { GET_DOC_SCAN_FIELDS } from "consts";

import { DocScanFieldRecord, DocScanFieldSortBy, DocScanSortOrder } from "models";

const DEFAULT_FIELDS_LIMIT = 20;
const FIELDS_SORT_BY: DocScanFieldSortBy = "createdAt";
const FIELDS_SORT_ORDER: DocScanSortOrder = "desc";

type UseDocScanFieldsPaginatedOptions = {
  docScanId: string;
  overallStatus?: string;
  excludeOverallStatus?: string;
  search?: string;
  pinRecord?: DocScanFieldRecord;
  enabled?: boolean;
  limit?: number;
  onNewFields?: (fields: DocScanFieldRecord[]) => void;
};

export const useDocScanFieldsPaginated = ({
  docScanId,
  overallStatus,
  excludeOverallStatus,
  search,
  pinRecord,
  enabled = true,
  limit = DEFAULT_FIELDS_LIMIT,
  onNewFields,
}: UseDocScanFieldsPaginatedOptions) => {
  const [page, setPage] = useState(1);
  const [loadedFields, setLoadedFields] = useState<DocScanFieldRecord[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Reset pagination when search or overall status changes
  useEffect(() => {
    setPage(1);
    setLoadedFields([]);
    setHasMore(false);
  }, [search, overallStatus]);

  const {
    data: fieldsData,
    isFetching,
    isError,
  } = useQuery({
    queryKey: [GET_DOC_SCAN_FIELDS, docScanId, page, limit, "public", overallStatus, excludeOverallStatus, search],
    queryFn: () =>
      listUserDocumentScanFields({
        id: docScanId,
        page,
        limit: limit,
        sortBy: FIELDS_SORT_BY,
        sortOrder: FIELDS_SORT_ORDER,
        overallStatus,
        excludeOverallStatus,
        search,
      }),
    retry: 1,
    enabled,
  });

  useEffect(() => {
    if (!fieldsData) return;

    setLoadedFields((prev) => {
      const newFields = page === 1 ? fieldsData.fields : [...prev, ...fieldsData.fields];

      if (pinRecord) {
        const filtered = newFields.filter((f) => f.id !== pinRecord.id);
        return [pinRecord, ...filtered];
      }

      return newFields;
    });

    setHasMore(fieldsData.count > page * limit);
    onNewFields?.(fieldsData.fields);
  }, [fieldsData, page, pinRecord]); // eslint-disable-line react-hooks/exhaustive-deps -- onNewFields is intentionally excluded to avoid re-running on callback identity changes

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isFetching) {
          setPage((p) => p + 1);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, isFetching, enabled]);

  const reset = () => {
    setPage(1);
    setLoadedFields([]);
    setHasMore(false);
  };

  return { loadedFields, isFetching, isError, hasMore, sentinelRef, reset };
};
